import axios from 'axios';
import { createContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { readStoredWorkspaceIdentity } from 'src/workspaces/AdminWorkspaceProvider';
import { getAdminWorkspaceContext } from 'src/queries/tenant/api';
import { parseMultiCompanySuccessEnvelope } from 'src/contracts/multiCompany/v1';

export type CompanyAppearanceOwner = { companyId: number; principalId: number; role: string };
export const CompanyAppearanceOwnerContext = createContext<CompanyAppearanceOwner | null>(null);

type AppearancePrincipal = { id: number; role: string };
export type CompanyThemeSelection = { configuration: unknown; [key: string]: unknown } | null;

/** One owner-scoped selection is shared by the stable shell and every Admin document. */
export function useCompanyThemeSelection(principal: AppearancePrincipal | null, ownerCompanyId?: number | string | null) {
  const parsedId = Number(ownerCompanyId);
  const companyId = Number.isSafeInteger(parsedId) && parsedId > 0 ? parsedId : null;
  return useQuery<CompanyThemeSelection>({
    queryKey: ['company-action-theme', principal?.id, principal?.role, companyId ?? null],
    enabled: Boolean(principal && companyId),
    queryFn: async () => {
      const prefix = principal?.role === 'SUPER_ADMIN' ? '/platform' : '';
      const response = await axios.get(`${prefix}/companies/${companyId}/profile/table-themes/selection`);
      const value = parseMultiCompanySuccessEnvelope<{ selected: CompanyThemeSelection }>(response.data);
      if (!value || !Object.prototype.hasOwnProperty.call(value.data, 'selected') || value.data.selected === undefined ||
          (value.data.selected !== null && (!value.data.selected?.configuration || typeof value.data.selected.configuration !== 'object'))) {
        throw new Error('Company appearance response is unavailable');
      }
      return value.data.selected;
    },
    retry: false,
    staleTime: 30000
  });
}

/** Resolve only the verified Admin company; public documents use their own owner instead. */
export function useCompanyAppearanceState() {
  const identity = readStoredWorkspaceIdentity();
  const { pathname } = useLocation();
  const principal = identity.kind === 'realm' ? identity.principal : null;
  const isCompanyAdmin = principal?.role === 'COMPANY_ADMIN';
  const workspace = useQuery({
    queryKey: ['admin-workspace-context', principal?.id],
    queryFn: getAdminWorkspaceContext,
    enabled: isCompanyAdmin,
    retry: false
  });
  const explicit = /^\/platform\/companies\/([1-9]\d*)(?:\/|$)/.exec(pathname);
  const verifiedWorkspace = isCompanyAdmin && workspace.data?.actorId === principal?.id ? workspace.data : null;
  const companyId = verifiedWorkspace?.company.id ?? (principal?.role === 'SUPER_ADMIN' && explicit ? Number(explicit[1]) : null);
  const selection = useCompanyThemeSelection(principal, companyId);
  const workspacePending = isCompanyAdmin && !verifiedWorkspace && workspace.isLoading;
  const workspaceError = isCompanyAdmin && (workspace.isError || (!workspace.isLoading && !verifiedWorkspace));
  const resolved = (!isCompanyAdmin || Boolean(verifiedWorkspace)) && (!companyId || selection.data !== undefined);
  return {
    resolved,
    owner: companyId && principal ? { companyId: Number(companyId), principalId: principal.id, role: principal.role } : null,
    configuration: companyId ? selection.data?.configuration ?? (selection.data === null ? null : undefined) : null,
    pending: workspacePending || Boolean(companyId && selection.data === undefined && selection.isLoading),
    error: Boolean(workspaceError || companyId && selection.isError),
    retry: () => { if (workspaceError) void workspace.refetch(); else void selection.refetch(); }
  };
}

export default function useCompanyActionConfiguration() {
  return useCompanyAppearanceState().configuration;
}
